/**
 * Client side of admin impersonation (Organizations -> org -> Impersonate).
 * The backend (routes/admin-impersonate.ts) mints a real session token for
 * the target user; this just swaps the Service-Worker-held bearer token
 * (see lib/sw.ts) to that one, stashing the super_admin's OWN token first
 * so "Exit impersonation" can restore it.
 *
 * A full page navigation on both start and stop is deliberate, not a
 * missed optimization: the WS singleton, its session-derived React state,
 * and the SW's token are all easiest to get into a clean, consistent state
 * by just reloading, rather than trying to tear down and rebuild a live
 * WS connection + AppShell session mid-flight.
 *
 * sessionStorage (not localStorage) holds the "return" state — per-tab,
 * cleared when the tab closes, never synced across tabs/devices. It's
 * read synchronously (see ImpersonationBanner), unlike the SW token which
 * requires an async postMessage round trip — that's the whole reason this
 * exists as a separate, ordinary storage layer instead of also going
 * through the SW.
 */
import { getToken, setToken } from "./sw";
import { api, ApiError } from "./api";

const STORAGE_KEY = "pure_impersonation";

export interface ImpersonationMeta {
  adminToken: string;
  target: { id: string; email: string; name: string | null };
  org: { id: string; name: string; slug: string };
}

export function getImpersonationMeta(): ImpersonationMeta | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ImpersonationMeta;
  } catch {
    return null;
  }
}

export async function startImpersonation(
  orgId: string,
  userId: string,
): Promise<void> {
  const adminToken = await getToken();
  if (!adminToken) throw new Error("not signed in");

  const res = await api<{
    ok: true;
    token: string;
    exp: number;
    target: ImpersonationMeta["target"];
    org: ImpersonationMeta["org"];
  }>("/api/admin/impersonate", {
    method: "POST",
    body: JSON.stringify({ org_id: orgId, user_id: userId }),
  });

  const meta: ImpersonationMeta = {
    adminToken,
    target: res.target,
    org: res.org,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(meta));
  await setToken(res.token);
  window.location.href = "/dashboard";
}

export async function stopImpersonation(): Promise<void> {
  const meta = getImpersonationMeta();
  // Best-effort: tell the backend to kill the impersonation session
  // server-side too, not just stop using it client-side. A failure here
  // (already-ended session, network blip) shouldn't block getting the
  // admin back into their own account.
  try {
    await api("/api/admin/impersonate/stop", { method: "POST" });
  } catch (err) {
    if (!(err instanceof ApiError)) throw err;
  }

  sessionStorage.removeItem(STORAGE_KEY);
  if (meta) {
    await setToken(meta.adminToken);
    window.location.href = "/admin/orgs";
  } else {
    // No return state (e.g. sessionStorage was cleared mid-impersonation)
    // — nothing to restore, so send them to sign in fresh rather than
    // leaving a dead session token in place.
    window.location.href = "/login";
  }
}
